<script setup>
import QRCode from '@chenfengyuan/vue-qrcode'
import { cookieRef } from '@/@layouts/stores/config'

definePage({
  meta: {
    name: 'utility-qrcode',
    subject: 'service_desk.menu',
    action: 'qr_code_generator',
  },
})

const avatarLocal = ref(null)
const refInputEl = ref()
const size = ref(250)

const unit = cookieRef('selectedUnit')
const locationType = ref()
const location = ref()

// The bed's QR credential, fetched per location. Never held anywhere but
// component state -- see the note on the fetch below.
const qrToken = ref(null)
const tokenLoading = ref(false)
const tokenError = ref(null)

const unitId = computed(() => unit.value?.id ?? unit.value ?? null)

// A ?t= URL carries the token and nothing else. The old base64 ?s=&l=&u=
// format is gone from this generator: the server resolves the feedback
// source, location and unit from the token itself, so encoding ids into the
// link only re-exposed an enumerable location id.
const qrcodeLink = computed(() => (
  qrToken.value
    ? `${window.location.origin}/service-desk/chat?t=${qrToken.value}`
    : null
))

const canRender = computed(() => Boolean(qrcodeLink.value))

// Guards against an out-of-order response: picking a location, then another
// before the first resolves, would otherwise let the stale token win and
// print a QR code for the wrong bed.
let tokenRequestId = 0

const fetchToken = async locationId => {
  const requestId = ++tokenRequestId

  qrToken.value = null
  tokenError.value = null

  if (!locationId) {
    tokenLoading.value = false

    return
  }

  tokenLoading.value = true

  try {
    const response = await $api(`/service-desk/qr/locations/${locationId}/token`, { method: 'POST' })

    if (requestId !== tokenRequestId) return

    qrToken.value = response?.qr_token ?? null

    if (!qrToken.value)
      tokenError.value = 'This location did not return a QR token.'
  }
  catch (error) {
    if (requestId !== tokenRequestId) return

    // $api already raises a toast for most statuses; `reported` says so, and
    // repeating it here would show the same sentence twice.
    tokenError.value = error?.reported
      ? 'Could not load the QR code for this location.'
      : (error?.data?.message ?? error?.message ?? 'Could not load the QR code for this location.')
  }
  finally {
    if (requestId === tokenRequestId)
      tokenLoading.value = false
  }
}

watch(location, selected => fetchToken(selected?.id ?? null))

// Both lists are filtered by unit, so a selection made under the previous
// unit is stale the moment the unit changes -- and a stale location would
// silently mint a token for a bed on another site.
watch(unitId, () => {
  locationType.value = null
  location.value = null
})

const selectedLocationTypeId = computed(() => locationType.value?.id ?? null)

const { data: locationTypes } = await useApi(createUrl('/masters/location-types', {
  query: {
    search: { unit_id: unitId },
    select: ['id', 'name'],
  },
}), {
  beforeFetch: ({ cancel }) => {
    if (!unitId.value)
      cancel()
  },
})

const { isFetching: locationLoading, data: locations } = useApi(createUrl('/masters/locations', {
  query: {
    search: {
      location_type_id: selectedLocationTypeId,
      unit_id: unitId,
    },
    select: ['id', 'name'],
  },
}), {
  beforeFetch: ({ cancel }) => {
    if (!(unitId.value && locationType.value))
      cancel()
  },
})

const changeAvatar = file => {
  const fileReader = new FileReader()
  const { files } = file.target
  if (files && files.length) {
    fileReader.readAsDataURL(files[0])
    fileReader.onload = () => {
      if (typeof fileReader.result === 'string')
        avatarLocal.value = fileReader.result

      const canvas = document.querySelector('canvas')
      if (canvas)
        onReady(canvas)
    }
  }
}

const onReady = canvas => {
  if (!canRender.value) return

  const context = canvas.getContext('2d')
  const image = new Image()

  // The bed label, printed under the code so a ward can tell two codes apart
  // without scanning them.
  context.font = 'bold 15px Arial'
  context.fillStyle = '#000'
  context.textAlign = 'center'
  context.fillText(location.value?.name ?? '', canvas.width / 2, canvas.height - 2)

  if (!avatarLocal.value) return

  image.src = avatarLocal.value
  image.onload = () => {
    const coverage = 0.15
    const width = Math.round(canvas.width * coverage)
    const x = (canvas.width - width) / 2

    image.setAttribute('crossorigin', 'anonymous')

    drawImage(context, image, x, x, width, width)
  }
}

const drawImage = (context, image, x, y, width, height, radius = 4) => {
  context.shadowOffsetX = 0
  context.shadowOffsetY = 2
  context.shadowBlur = 4
  context.shadowColor = '#00000040'
  context.lineWidth = 8
  context.beginPath()
  context.moveTo(x + radius, y)
  context.arcTo(x + width, y, x + width, y + height, radius)
  context.arcTo(x + width, y + height, x, y + height, radius)
  context.arcTo(x, y + height, x, y, radius)
  context.arcTo(x, y, x + width, y, radius)
  context.closePath()
  context.strokeStyle = '#fff'
  context.stroke()
  context.clip()
  context.fillStyle = '#fff'
  context.fillRect(x, x, width, height)
  context.drawImage(image, x, x, width, height)
}

const downloadOptions = computed(() => ({
  name: [locationType.value?.name, location.value?.name].filter(Boolean).join(' - ') || 'qr-code',
  extension: 'png',
}))

const downloadQRCode = () => {
  const canvas = document.querySelector('canvas')
  if (!canvas) return

  const link = document.createElement('a')

  link.download = `${downloadOptions.value.name}.${downloadOptions.value.extension}`
  link.href = canvas.toDataURL()
  link.click()
}

const copyQRCodeLink = () => {
  if (qrcodeLink.value)
    navigator.clipboard.writeText(qrcodeLink.value)
}
</script>

<template>
  <div>
    <!-- Page Header -->
    <div class="d-flex flex-wrap align-center justify-space-between gap-4 mb-6">
      <div>
        <div class="text-h5">
          QR Code Generator
        </div>
        <span class="text-body-2 text-medium-emphasis">
          Generate a bedside QR code that opens the service desk assistant for one location
        </span>
      </div>

      <div class="d-flex gap-3">
        <VBtn
          variant="tonal"
          color="primary"
          prepend-icon="tabler-copy"
          :disabled="!canRender"
          @click="copyQRCodeLink"
        >
          Copy Link
        </VBtn>
        <VBtn
          color="primary"
          prepend-icon="tabler-download"
          :disabled="!canRender"
          @click="downloadQRCode"
        >
          Download QR
        </VBtn>
      </div>
    </div>

    <VRow>
      <!-- Configuration Panel -->
      <VCol
        cols="12"
        md="5"
      >
        <VCard
          variant="flat"
          class="border"
        >
          <VCardItem>
            <template #title>
              <span class="text-h6 font-weight-medium">Configuration</span>
            </template>
            <template #subtitle>
              <span class="text-body-2 text-medium-emphasis">Choose a location and customise the code</span>
            </template>
          </VCardItem>

          <VDivider />

          <VCardText class="d-flex flex-column gap-4">
            <!-- Logo Upload -->
            <div>
              <VLabel class="mb-1 text-body-2 text-high-emphasis">
                Logo Image
              </VLabel>
              <div
                class="upload-zone rounded-lg cursor-pointer d-flex align-center gap-3 pa-3"
                @click="refInputEl.click()"
              >
                <VAvatar
                  v-if="avatarLocal"
                  :image="avatarLocal"
                  size="48"
                  rounded="lg"
                />
                <VAvatar
                  v-else
                  size="48"
                  rounded="lg"
                  color="primary"
                  variant="tonal"
                >
                  <VIcon
                    icon="tabler-photo-plus"
                    size="24"
                  />
                </VAvatar>
                <div>
                  <span class="text-body-2 font-weight-medium d-block">
                    {{ avatarLocal ? 'Change logo' : 'Add a logo' }}
                  </span>
                  <span class="text-caption text-medium-emphasis">
                    PNG, JPG up to 2MB
                  </span>
                </div>
              </div>
              <input
                ref="refInputEl"
                type="file"
                name="file"
                accept=".jpeg,.png,.jpg,GIF"
                hidden
                @input="changeAvatar"
              >
            </div>

            <!-- Location Type -->
            <AppSelect
              v-model="locationType"
              :disabled="!unitId"
              :items="locationTypes ?? []"
              :item-title="item => item.name"
              :item-value="item => item.id"
              eager
              clearable
              return-object
              placeholder="Select location type"
              label="Location Type"
              :menu-props="{ maxHeight: '200px' }"
            />

            <!-- Location -->
            <AppAutocomplete
              v-model="location"
              :disabled="!(unitId && locationType)"
              :loading="locationLoading"
              :items="locations ?? []"
              :item-title="item => item.name"
              :item-value="item => item.id"
              eager
              clearable
              required
              return-object
              placeholder="Search location"
              label="Location"
              :menu-props="{ maxHeight: '200px' }"
            />

            <!-- QR Size Slider -->
            <div>
              <div class="d-flex align-center justify-space-between mb-1">
                <VLabel class="text-body-2 text-high-emphasis">
                  QR Size
                </VLabel>
                <VChip
                  size="small"
                  variant="tonal"
                  color="primary"
                  label
                >
                  {{ size }}px
                </VChip>
              </div>
              <VSlider
                v-model="size"
                thumb-label
                :max="550"
                :min="100"
                :step="50"
                color="primary"
                track-color="grey-lighten-3"
                style="margin-inline-start: 0px; margin-inline-end: 0px;"
              />
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <!-- QR Code Preview Panel -->
      <VCol
        cols="12"
        md="7"
      >
        <VCard
          variant="flat"
          class="border"
        >
          <VCardItem>
            <template #title>
              <span class="text-h6 font-weight-medium">QR Preview</span>
            </template>
            <template #subtitle>
              <span class="text-body-2 text-medium-emphasis">Live preview of the generated QR code</span>
            </template>
          </VCardItem>

          <VDivider />

          <VCardText>
            <div class="qr-preview-area d-flex flex-column align-center justify-center rounded-lg pa-8">
              <!-- Fetching the location's token -->
              <div
                v-if="tokenLoading"
                class="text-center"
              >
                <VProgressCircular
                  indeterminate
                  color="primary"
                  size="40"
                  class="mb-3"
                />
                <div class="text-body-2 text-medium-emphasis">
                  Preparing the code for {{ location?.name }}
                </div>
              </div>

              <!--
                The token could not be fetched. Showing a QR code here would
                encode a broken link onto a ward wall.
              -->
              <div
                v-else-if="tokenError"
                class="text-center"
              >
                <VAvatar
                  size="64"
                  color="error"
                  variant="tonal"
                  class="mb-3"
                >
                  <VIcon
                    icon="tabler-alert-triangle"
                    size="32"
                  />
                </VAvatar>
                <div class="text-body-1 font-weight-medium mb-1">
                  No QR Code Generated
                </div>
                <span class="text-body-2 text-medium-emphasis">
                  {{ tokenError }}
                </span>
              </div>

              <template v-else-if="canRender">
                <QRCode
                  :value="qrcodeLink"
                  :options="{
                    errorCorrectionLevel: 'Q',
                    width: size,
                  }"
                  @ready="onReady"
                />
                <div class="text-caption text-medium-emphasis mt-4 text-center">
                  Scanning this opens the service desk assistant for
                  <span class="font-weight-medium">{{ location?.name }}</span>
                </div>
              </template>

              <div
                v-else
                class="text-center"
              >
                <VAvatar
                  size="64"
                  color="primary"
                  variant="tonal"
                  class="mb-3"
                >
                  <VIcon
                    icon="tabler-qrcode"
                    size="32"
                  />
                </VAvatar>
                <div class="text-body-1 font-weight-medium mb-1">
                  No QR Code Yet
                </div>
                <span class="text-body-2 text-medium-emphasis">
                  {{ unitId ? 'Select a location to generate your QR code' : 'Select a unit to generate your QR code' }}
                </span>
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </div>
</template>

<style lang="scss" scoped>
.upload-zone {
  border: 1px dashed rgba(var(--v-border-color), var(--v-border-opacity));
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(var(--v-theme-primary), 0.5);
    background: rgba(var(--v-theme-primary), 0.04);
  }
}

.qr-preview-area {
  background: rgba(var(--v-theme-primary), 0.02);
  border: 1px dashed rgba(var(--v-border-color), var(--v-border-opacity));
  min-block-size: 350px;
}
</style>
